import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Search, User, Menu, X, LogOut, LayoutDashboard, Store } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const headerBackground = useTransform(scrollY, [0, 100], ["rgba(11, 11, 13, 0)", "rgba(11, 11, 13, 0.9)"]);
  const headerBlur = useTransform(scrollY, [0, 100], ["blur(0px)", "blur(12px)"]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "Categories", path: "/categories" },
    { name: "Sell with OrvDigital", path: "/sell" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const handleLogout = () => {
    logout();
    setAccountOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/");
  };

  return (
    <motion.header
      style={{ backgroundColor: headerBackground, backdropFilter: headerBlur, WebkitBackdropFilter: headerBlur }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "border-b border-white/5" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center group" data-testid="header-logo">
            <motion.img src="/assets/orvdigital-logo-white.png" alt="OrvDigital" className="h-9 w-auto transition-opacity duration-300 group-hover:opacity-80" whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }} />
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors relative group">
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-purple-500 group-hover:w-full transition-all duration-300" />
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <button onClick={() => navigate("/products")} className="p-2 text-gray-300 hover:text-white transition-colors" aria-label="Search">
              <Search size={20} />
            </button>

            {user ? (
              <div className="relative">
                <button onClick={() => setAccountOpen(!accountOpen)} data-testid="account-menu-btn" className="p-2 text-gray-300 hover:text-white transition-colors flex items-center gap-1">
                  <User size={20} />
                </button>
                {accountOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setAccountOpen(false)} />
                    <div className="absolute right-0 mt-2 w-52 bg-[#0B0B0D] border border-white/10 rounded-xl shadow-2xl py-2 z-20">
                      <div className="px-4 py-2 border-b border-white/5">
                        <p className="text-sm text-white truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <Link to="/dashboard" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5">
                        <LayoutDashboard size={16} /> My OrvDigital
                      </Link>
                      {(user.role === "partner" || user.role === "admin") && (
                        <Link to="/partner" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5">
                          <Store size={16} /> Seller Hub
                        </Link>
                      )}
                      {user.role === "admin" && (
                        <Link to="/admin" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5">
                          <LayoutDashboard size={16} /> Admin
                        </Link>
                      )}
                      <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5">
                        <LogOut size={16} /> Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link to="/login" className="p-2 text-gray-300 hover:text-white transition-colors" aria-label="Login" data-testid="login-icon">
                <User size={20} />
              </Link>
            )}

            <Button onClick={() => navigate("/products")} className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-medium px-5 py-2 rounded-lg shadow-lg shadow-purple-500/20">
              Explore Products
            </Button>
          </div>
          <div className="lg:hidden flex items-center gap-2">
  <button
    onClick={() => navigate("/products")}
    className="p-2 text-gray-300 hover:text-white"
    aria-label="Search"
  >
    <Search size={22} />
  </button>

  <button
    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
    className="p-2 text-gray-300 hover:text-white"
    aria-label="Toggle menu"
    data-testid="mobile-menu-btn"
  >
    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
  </button>
</div>
        
      </div>
      </div>

      <motion.div
        initial={false}
        animate={{ height: isMobileMenuOpen ? "auto" : 0, opacity: isMobileMenuOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="lg:hidden overflow-hidden bg-[#0B0B0D]/95 backdrop-blur-xl border-t border-white/5"
      >
        <div className="px-4 py-6 space-y-2">
          
          {navLinks.map((link) => (
            <Link key={link.path} to={link.path} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg">
              {link.name}
            </Link>
          ))}
          <div className="border-t border-white/5 pt-2 mt-2">
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-base text-gray-300 hover:bg-white/5 rounded-lg">My OrvDigital</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-base text-gray-300 hover:bg-white/5 rounded-lg">Admin</Link>}
                <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-base text-gray-300 hover:bg-white/5 rounded-lg">Log out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-2 text-base text-gray-300 hover:bg-white/5 rounded-lg">Log in / Sign up</Link>
            )}
          </div>
          
        </div>
      </motion.div>
    </motion.header>
  );
};
